import React, { useState } from 'react';
import { X, Wallet, CheckCircle, AlertCircle } from 'lucide-react';
import apiService from '../../apiService';

const QUICK_AMOUNTS = [100, 250, 500, 1000];

const WalletTopupModal = ({ isOpen, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('enter'); // 'enter' | 'success'
  const [newBalance, setNewBalance] = useState(0);

  if (!isOpen) return null;

  const handleQuickAmount = (val) => {
    setAmount(String(val));
    setError('');
  };

  const handleAmountChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setAmount(val);
    setError('');
  };

  const handleTopup = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 10) {
      setError('Minimum top-up amount is ₹10');
      return;
    }
    if (amt > 10000) {
      setError('Maximum top-up amount is ₹10,000');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Initiate top-up and get gateway order
      const initiateResp = await apiService.initiateWalletTopup(amt);
      if (!initiateResp?.success) {
        throw new Error(initiateResp?.message || 'Failed to initiate top-up');
      }

      const { gateway_order_id, pending_transaction_id } = initiateResp.data || initiateResp;

      // Step 2: Open Razorpay (if available), otherwise simulate for dev
      if (window.Razorpay) {
        const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY;
        const options = {
          key: razorpayKey,
          amount: amt * 100,
          currency: 'INR',
          name: 'Deal Store',
          description: 'Wallet Top-up',
          order_id: gateway_order_id,
          handler: async (response) => {
            try {
              const verifyResp = await apiService.verifyWalletTopup({
                pending_transaction_id,
                transaction_id: response.razorpay_payment_id,
                gateway_response: response,
              });
              if (verifyResp?.success) {
                const balance = verifyResp.data?.new_balance ?? 0;
                setNewBalance(balance);
                setStep('success');
                onSuccess && onSuccess(balance);
              } else {
                setError(verifyResp?.message || 'Payment verification failed');
              }
            } catch (err) {
              setError('Payment verification failed. Please contact support.');
            }
          },
          prefill: { name: '', email: '', contact: '' },
          theme: { color: '#2563EB' },
          modal: { ondismiss: () => setLoading(false) },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
        setLoading(false);
      } else {
        // Dev/demo: simulate successful payment
        const verifyResp = await apiService.verifyWalletTopup({
          pending_transaction_id,
          transaction_id: 'demo_txn_' + Date.now(),
          gateway_response: { demo: true },
        });
        if (verifyResp?.success) {
          const balance = verifyResp.data?.new_balance ?? 0;
          setNewBalance(balance);
          setStep('success');
          onSuccess && onSuccess(balance);
        } else {
          setError(verifyResp?.message || 'Top-up failed');
        }
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    setStep('enter');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-t-3xl p-6 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Wallet size={22} className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-800">Add Money to Wallet</h2>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {step === 'success' ? (
          <div className="text-center py-6">
            <CheckCircle size={56} className="text-green-500 mx-auto mb-3" />
            <p className="text-xl font-bold text-gray-800 mb-1">Top-up Successful!</p>
            <p className="text-gray-500 mb-2">₹{parseFloat(amount).toFixed(2)} added to your wallet</p>
            <p className="text-blue-600 font-semibold text-lg">New Balance: ₹{newBalance.toFixed(2)}</p>
            <button
              onClick={handleClose}
              className="mt-5 w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Quick select */}
            <p className="text-sm text-gray-500 mb-2">Quick Select</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {QUICK_AMOUNTS.map((val) => (
                <button
                  key={val}
                  onClick={() => handleQuickAmount(val)}
                  className={`py-2 rounded-xl text-sm font-semibold border-2 transition-colors ${
                    amount === String(val)
                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                      : 'border-gray-200 text-gray-700 hover:border-blue-300'
                  }`}
                >
                  ₹{val}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <p className="text-sm text-gray-500 mb-2">Or enter amount</p>
            <div className="relative mb-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">₹</span>
              <input
                type="number"
                min="10"
                max="10000"
                value={amount}
                onChange={handleAmountChange}
                placeholder="Enter amount"
                className="w-full border-2 border-gray-200 rounded-xl pl-9 pr-4 py-3 text-lg font-semibold focus:outline-none focus:border-blue-500"
              />
            </div>
            <p className="text-xs text-gray-400 mb-4">Min ₹10 · Max ₹10,000</p>

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-3 py-2 mb-4 text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              onClick={handleTopup}
              disabled={loading || !amount}
              className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Processing...' : `Add ₹${amount || '0'} to Wallet`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default WalletTopupModal;
